import React from "react";
import { useAppSelector } from "../state/hooks";
import { useJobcardManpowerAllocation } from "../hooks/useJobcardManpowerAllocation";
import type { JobcardManpowerDraftTime } from "../types/jobcardManpower";
import type { JobcardManpowerEmployee } from "../types/jobcardManpower";
import { JobcardManpowerDetailsPanel } from "./JobcardManpowerDetailsPanel";
import { JobcardManpowerLineItemsPanel } from "./JobcardManpowerLineItemsPanel";
import type { JobcardManpowerAssignedLine } from "../types/jobcardManpower";
import {
  JOBCARD_MANPOWER_HOUR_OPTIONS,
  JOBCARD_MANPOWER_MINUTE_OPTIONS,
  formatJobcardManpowerRateBasisLabel,
  jobcardManpowerEstCost,
  jobcardManpowerFormatCurrency,
  jobcardManpowerHasOvertime,
  jobcardManpowerInitials,
} from "../utils/jobcardManpowerUtils";

const TimeSelects: React.FC<{
  label: string;
  value: JobcardManpowerDraftTime;
  onChange: (next: JobcardManpowerDraftTime) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <div className="flex gap-1">
      <select
        className="rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs px-1 py-1 min-w-[52px]"
        value={value.regular_hours}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, regular_hours: Number(e.target.value) })
        }
        aria-label={`${label} hours`}
      >
        {JOBCARD_MANPOWER_HOUR_OPTIONS.map((h) => (
          <option key={`${label}-h-${h}`} value={h}>
            {h}h
          </option>
        ))}
      </select>
      <select
        className="rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs px-1 py-1 min-w-[52px]"
        value={value.regular_minutes}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, regular_minutes: Number(e.target.value) })
        }
        aria-label={`${label} minutes`}
      >
        {JOBCARD_MANPOWER_MINUTE_OPTIONS.map((m) => (
          <option key={`${label}-m-${m}`} value={m}>
            {m}m
          </option>
        ))}
      </select>
    </div>
  </div>
);

const OvertimeSelects: React.FC<{
  value: JobcardManpowerDraftTime;
  onChange: (next: JobcardManpowerDraftTime) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      Overtime
    </span>
    <div className="flex gap-1">
      <select
        className="rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs px-1 py-1 min-w-[52px]"
        value={value.overtime_hours}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, overtime_hours: Number(e.target.value) })
        }
        aria-label="Overtime hours"
      >
        {JOBCARD_MANPOWER_HOUR_OPTIONS.map((h) => (
          <option key={`ot-h-${h}`} value={h}>
            {h}h
          </option>
        ))}
      </select>
      <select
        className="rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs px-1 py-1 min-w-[52px]"
        value={value.overtime_minutes}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, overtime_minutes: Number(e.target.value) })
        }
        aria-label="Overtime minutes"
      >
        {JOBCARD_MANPOWER_MINUTE_OPTIONS.map((m) => (
          <option key={`ot-m-${m}`} value={m}>
            {m}m
          </option>
        ))}
      </select>
    </div>
  </div>
);

export const JobcardManpowerAllocationModal: React.FC = () => {
  const { isOpen, context } = useAppSelector((s) => s.jobcardManpowerModal);
  const mp = useJobcardManpowerAllocation(isOpen, context);

  if (!isOpen || !context) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="jobcard-manpower-title"
    >
      <div className="flex flex-col w-full max-w-6xl max-h-[92vh] rounded-xl overflow-hidden bg-white dark:bg-gray-900 shadow-2xl">
        <div className="flex items-start justify-between px-5 py-4 bg-[#1B5E20] text-white shrink-0">
          <div>
            <h2 id="jobcard-manpower-title" className="text-lg font-semibold">
              Assign Employees
            </h2>
            {mp.subtitle ? (
              <p className="text-sm text-white/85 mt-0.5">{mp.subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="p-1 rounded hover:bg-white/15"
            onClick={mp.close}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <JobcardManpowerDetailsPanel
            rows={mp.jobDetailRows}
            loading={mp.loading && mp.jobDetailRows.every((r) => r.value === "—")}
          />

          <JobcardManpowerLineItemsPanel
            lineItems={mp.jobcardLineItems}
            loading={mp.loading}
          />

          {mp.loading ? (
            <p className="text-sm text-gray-500 text-center py-2">
              Loading saved assignment for this job card…
            </p>
          ) : mp.hasSavedRecord && mp.assigned.length > 0 ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50/90 dark:bg-blue-950/30 dark:border-blue-800 px-3 py-2 text-sm text-blue-900 dark:text-blue-100">
              Saved assignment loaded — {mp.assigned.length} employee
              {mp.assigned.length === 1 ? "" : "s"} on this job card. You can add
              more or update times below, then save again.
            </div>
          ) : !mp.loading && mp.assigned.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/90 dark:bg-amber-950/25 dark:border-amber-800 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
              No manpower assigned yet for this job card. Select employees and
              times, then click Save assignment.
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Labour summary
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2 text-center">
                  <p className="text-lg font-bold">{mp.summary.employees}</p>
                  <p className="text-[10px] text-gray-500">Employees</p>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2 text-center">
                  <p className="text-lg font-bold">{mp.summary.timeLabel}</p>
                  <p className="text-[10px] text-gray-500">Time</p>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2 text-center">
                  <p className="text-lg font-bold">
                    {jobcardManpowerFormatCurrency(mp.summary.cost)}
                  </p>
                  <p className="text-[10px] text-gray-500">Cost</p>
                </div>
              </div>
              <label className="block mt-3 text-xs text-gray-600 dark:text-gray-400">
                Total labour cost (optional — distributes by hours)
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  className="mt-1 w-full rounded border border-gray-200 dark:border-gray-600 px-2 py-1 text-sm dark:bg-gray-800"
                  value={mp.totalLabourCost}
                  onChange={(e) =>
                    mp.setTotalLabourCost(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="Auto from line rates"
                />
              </label>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 min-h-[120px]">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase">
                  Assigned employees
                </p>
                <span className="text-xs rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5">
                  {mp.assigned.length} assigned
                </span>
              </div>
              {mp.assigned.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">
                  No employees assigned yet
                </p>
              ) : (
                <div className="overflow-x-auto max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                        <th className="py-1 pr-2">Name</th>
                        <th className="py-1 pr-2">Regular</th>
                        <th className="py-1 pr-2">OT</th>
                        <th className="py-1 pr-2">Rate</th>
                        <th className="py-1 pr-2">Cost</th>
                        <th className="py-1 w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {mp.assigned.map((l: JobcardManpowerAssignedLine) => {
                        const lineHasOt =
                          (l.overtime_hours || 0) > 0 ||
                          (l.overtime_minutes || 0) > 0;
                        return (
                        <tr
                          key={l.employeeid}
                          className="border-b border-gray-100 dark:border-gray-800"
                        >
                          <td className="py-1.5 pr-2 font-medium">
                            {l.employee_name || l.employeeid}
                          </td>
                          <td className="py-1.5 pr-2 whitespace-nowrap">
                            {l.regular_hours}h {l.regular_minutes}m
                          </td>
                          <td className="py-1.5 pr-2 whitespace-nowrap">
                            {l.overtime_hours}h {l.overtime_minutes}m
                          </td>
                          <td className="py-1.5 pr-2 whitespace-nowrap">
                            {lineHasOt ? (
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="w-20 rounded border border-gray-200 dark:border-gray-600 px-1 py-0.5 text-xs dark:bg-gray-800"
                                value={l.man_hr_rate || ""}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  mp.handleUpdateAssignedRate(
                                    l.employeeid,
                                    raw === "" ? 0 : Number(raw)
                                  );
                                }}
                                aria-label={`Hourly rate for ${l.employee_name}`}
                              />
                            ) : (
                              <span className="text-gray-500">
                                {jobcardManpowerFormatCurrency(l.man_hr_rate)}
                                /hr
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 whitespace-nowrap">
                            {jobcardManpowerFormatCurrency(
                              l.allocated_cost || l.amount
                            )}
                          </td>
                          <td className="py-1.5">
                            <button
                              type="button"
                              className="text-red-500 hover:underline"
                              onClick={() =>
                                mp.handleRemoveAssigned(l.employeeid)
                              }
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50/90 dark:bg-emerald-950/35 dark:border-emerald-800 px-3 py-2.5 text-xs text-emerald-950 dark:text-emerald-50"
            role="status"
          >
            <p className="font-semibold text-sm">
              {formatJobcardManpowerRateBasisLabel(mp.rateBasis)}
            </p>
            <p className="mt-0.5 text-emerald-800 dark:text-emerald-200/90">
              Hourly rate is taken from the employee master field{" "}
              <strong>Cost allocation amt (hourly)</strong>
              {mp.rateBasis.default_hourly_rate != null
                ? ` (default ${jobcardManpowerFormatCurrency(mp.rateBasis.default_hourly_rate)}/hr when not set)`
                : ""}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <input
                type="search"
                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 pl-8 pr-3 py-2 text-sm dark:bg-gray-800"
                placeholder="Search employee..."
                value={mp.search}
                onChange={(e) => mp.setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void mp.loadData();
                }}
              />
              <span className="absolute left-2.5 top-2.5 text-gray-400 text-sm">
                🔍
              </span>
            </div>
            <span className="text-xs text-gray-500">
              {mp.employees.length} available
            </span>
            <button
              type="button"
              className="text-xs text-primary-600 hover:underline"
              onClick={() => void mp.loadData()}
            >
              Refresh
            </button>
          </div>

          {mp.loading ? (
            <p className="text-center text-sm text-gray-500 py-8">Loading...</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 text-left text-[10px] uppercase text-gray-500">
                    <th className="p-2">Employee</th>
                    <th className="p-2">Role</th>
                    <th className="p-2">
                      Rate
                      <span className="block font-normal normal-case text-[9px] text-gray-400 mt-0.5">
                        per hour
                      </span>
                    </th>
                    <th className="p-2">Regular time</th>
                    <th className="p-2">Overtime</th>
                    <th className="p-2">Est. cost</th>
                    <th className="p-2 w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mp.employees.map((emp: JobcardManpowerEmployee) => {
                    const d = mp.getDraft(emp.id);
                    const regRate = mp.defaultHourlyRate(emp);
                    const otRate = mp.getDraftOtRate(emp);
                    const cost = jobcardManpowerEstCost(regRate, d);
                    const isAssigned = mp.isEmployeeAssigned(emp.id);
                    const overtimeEditable = jobcardManpowerHasOvertime(d);
                    return (
                      <tr
                        key={emp.id}
                        className="border-t border-gray-100 dark:border-gray-800"
                      >
                        <td className="p-2">
                          <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-full bg-emerald-700 text-white text-xs flex items-center justify-center font-semibold">
                              {jobcardManpowerInitials(emp.name)}
                            </span>
                            <span>{emp.name}</span>
                          </div>
                        </td>
                        <td className="p-2 text-gray-600">{emp.role || "—"}</td>
                        <td className="p-2 whitespace-nowrap">
                          {overtimeEditable ? (
                            <div className="flex flex-col gap-0.5">
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="w-24 rounded border border-amber-300 dark:border-amber-600 bg-amber-50/50 dark:bg-amber-950/30 px-1.5 py-1 text-xs"
                                value={otRate === 0 ? "" : otRate}
                                disabled={isAssigned}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  mp.setDraftRate(
                                    emp.id,
                                    raw === "" ? 0 : Number(raw)
                                  );
                                }}
                                aria-label={`Hourly rate for ${emp.name}`}
                              />
                              <span className="text-[9px] text-amber-700 dark:text-amber-400">
                                OT rate editable
                              </span>
                            </div>
                          ) : (
                            <span>
                              {jobcardManpowerFormatCurrency(
                                mp.defaultHourlyRate(emp)
                              )}
                              /hr
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          <TimeSelects
                            label="Regular"
                            value={d}
                            onChange={(v) =>
                              mp.setDraft(emp.id, {
                                ...d,
                                regular_hours: v.regular_hours,
                                regular_minutes: v.regular_minutes,
                              })
                            }
                            disabled={isAssigned}
                          />
                        </td>
                        <td className="p-2">
                          <OvertimeSelects
                            value={d}
                            onChange={(v) => {
                              const next = {
                                ...d,
                                overtime_hours: v.overtime_hours,
                                overtime_minutes: v.overtime_minutes,
                              };
                              const hadOt = jobcardManpowerHasOvertime(d);
                              const hasOt = jobcardManpowerHasOvertime(next);
                              if (hasOt && !hadOt) {
                                next.hourly_rate = mp.defaultHourlyRate(emp);
                              }
                              if (!hasOt) {
                                delete next.hourly_rate;
                              }
                              mp.setDraft(emp.id, next);
                            }}
                            disabled={isAssigned}
                          />
                        </td>
                        <td className="p-2 font-medium">
                          {jobcardManpowerFormatCurrency(cost)}
                        </td>
                        <td className="p-2">
                          <button
                            type="button"
                            disabled={isAssigned}
                            className="rounded bg-[#1B5E20] text-white text-xs px-2 py-1 disabled:opacity-40"
                            onClick={() => mp.handleAssign(emp)}
                          >
                            {isAssigned ? "Assigned" : "+ Assign"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            type="button"
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
            onClick={mp.close}
            disabled={mp.saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-[#1B5E20] text-white text-sm font-medium disabled:opacity-50"
            onClick={() => void mp.handleSave()}
            disabled={mp.saving || mp.loading}
          >
            {mp.saving ? "Saving..." : "Save assignment"}
          </button>
        </div>
      </div>
    </div>
  );
};
